import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import ModuleLayout from "@/components/admin/ModuleLayout"
import { inputCls, labelCls, btnSave, btnSecondary } from "@/components/admin/FormComponents"
import { staffRolesAdmin } from "@/lib/api/staffRolesClient"
import { getErrorMessage } from "@/lib/errors"
import {
  PERMISSION_ACTIONS,
  PERMISSION_ACTION_LABELS,
  PERMISSION_CATALOG,
  type PermissionAction,
  type PermissionKey,
  type PermissionResourceId,
} from "@alascorporation/access-control"
import { LoadState } from "@/components/ui/LoadState"

// Display order for the permission table's sections — mirrors AppSidebar.tsx's
// real nav structure so a role's editable sections line up with where they
// actually live for a logged-in user. Sub-group order only matters for groups
// that have them; a group without an entry here renders as one flat section.
const GROUP_ORDER = ["General", "Academia", "CCCD", "Tripoli Colombia", "Sistema"]
const SUB_GROUP_ORDER: Record<string, string[]> = {
  Academia: ["Docencia", "Catálogo y estructura"],
  CCCD: ["General", "Eventos"],
}

type CatalogEntry = (typeof PERMISSION_CATALOG)[number]

function buildSections(): { key: string; heading: string; rows: CatalogEntry[] }[] {
  const sections: { key: string; heading: string; rows: CatalogEntry[] }[] = []
  for (const group of GROUP_ORDER) {
    const subGroups = SUB_GROUP_ORDER[group]
    if (subGroups) {
      for (const subGroup of subGroups) {
        const rows = PERMISSION_CATALOG.filter((r) => r.group === group && r.subGroup === subGroup)
        if (rows.length > 0) sections.push({ key: `${group}-${subGroup}`, heading: `${group} — ${subGroup}`, rows })
      }
    } else {
      const rows = PERMISSION_CATALOG.filter((r) => r.group === group && !r.subGroup)
      if (rows.length > 0) sections.push({ key: group, heading: group, rows })
    }
  }
  return sections
}

const SECTIONS = buildSections()

export default function RoleFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [name, setName] = useState("")
  const [selected, setSelected] = useState<Set<PermissionKey>>(new Set())
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    staffRolesAdmin
      .get(id)
      .then((role) => {
        if (cancelled) return
        setName(role.name)
        setSelected(new Set(role.permissionKeys))
      })
      .catch((err) => !cancelled && setError(getErrorMessage(err)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [id])

  // Ver es prerequisito de crear/editar/eliminar (ninguna de esas acciones
  // tiene sentido sin poder ver el registro primero — ver
  // findMissingViewImplications en @alascorporation/access-control, que el
  // backend usa para rechazar esta misma combinación si llegara a colarse).
  // Por eso: marcar cualquier otra acción marca Ver automáticamente, y
  // desmarcar Ver desmarca las demás acciones de ese recurso.
  function toggleAction(resource: PermissionResourceId, action: PermissionAction) {
    const key = `${resource}:${action}` as PermissionKey
    setSelected((prev) => {
      const next = new Set(prev)
      const turningOn = !next.has(key)

      if (turningOn) {
        next.add(key)
        if (action !== "view") next.add(`${resource}:view` as PermissionKey)
      } else {
        next.delete(key)
        if (action === "view") {
          for (const otherAction of PERMISSION_ACTIONS) {
            if (otherAction !== "view") next.delete(`${resource}:${otherAction}` as PermissionKey)
          }
        }
      }
      return next
    })
  }

  function goBack() {
    navigate("/settings/roles")
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true)
    setError("")
    try {
      const permissionKeys = Array.from(selected)
      if (isEdit && id) {
        await staffRolesAdmin.update(id, { name, permissionKeys })
      } else {
        await staffRolesAdmin.create({ name, permissionKeys })
      }
      qc.invalidateQueries({ queryKey: ["staff-roles"] })
      goBack()
    } catch (err) {
      setError(getErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Sistema",
        { label: "Roles y permisos", to: "/settings/roles" },
        isEdit ? "Editar" : "Nuevo",
      ]}
      title={isEdit ? "Editar rol" : "Nuevo rol"}
      subtitle="Elige, sección por sección, si este rol puede ver, crear, editar o eliminar"
      tabs={[]}
      metrics={[]}
    >
      {loading ? (
        <LoadState />
      ) : (
        <form onSubmit={onSubmit}>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <label className={labelCls}>Nombre del rol *</label>
            <input
              required
              autoFocus
              placeholder="Ej. Soporte técnico"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputCls} max-w-sm`}
            />
          </div>

          <div className="mt-5 space-y-5">
            {SECTIONS.map((section) => (
              <div
                key={section.key}
                className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]"
              >
                <h3 id={`section-heading-${section.key}`} className="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">
                  {section.heading}
                </h3>
                <div className="overflow-x-auto">
                  <table aria-labelledby={`section-heading-${section.key}`} className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-500">
                        <th scope="col" className="py-2 pr-4 font-medium">Recurso</th>
                        {PERMISSION_ACTIONS.map((action) => (
                          <th key={action} scope="col" className="w-24 px-2 py-2 text-center font-medium">
                            {PERMISSION_ACTION_LABELS[action]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row) => (
                        <tr key={row.resource} className="border-t border-gray-100 dark:border-gray-800">
                          <th scope="row" className="py-2.5 pr-4 text-left font-normal text-gray-700 dark:text-gray-300">
                            {row.label}
                          </th>
                          {PERMISSION_ACTIONS.map((action) => {
                            const supported = (row.actions as readonly PermissionAction[]).includes(action)
                            const key = `${row.resource}:${action}` as PermissionKey
                            return (
                              <td key={action} className="px-2 py-2.5 text-center">
                                {supported ? (
                                  <label className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center">
                                    <input
                                      type="checkbox"
                                      checked={selected.has(key)}
                                      onChange={() => toggleAction(row.resource, action)}
                                      className="rounded"
                                      aria-label={`${row.label} — ${PERMISSION_ACTION_LABELS[action]}`}
                                    />
                                  </label>
                                ) : (
                                  <span aria-hidden="true" className="text-gray-300 dark:text-gray-700">—</span>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {selected.size === 0 ? (
            <p role="status" aria-live="polite" className="mt-5 text-sm text-warning-600">
              Este rol no tiene ningún permiso seleccionado — quien lo tenga asignado no podrá ver ninguna sección del panel.
            </p>
          ) : null}

          {error ? (
            <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
              {error}
            </p>
          ) : null}

          <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
            <button type="button" onClick={goBack} className={`min-h-11 ${btnSecondary}`}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={`min-h-11 ${btnSave}`}>
              {saving ? "Guardando..." : isEdit ? "Guardar" : "Crear rol"}
            </button>
          </div>
        </form>
      )}
    </ModuleLayout>
  )
}
